import shortid from 'shortid';
import compareDesc from 'date-fns/compareDesc';
import { matchPath } from 'react-router-dom';

import { isIso8601Date } from '@zr/utilities/date';

import {
  PROFILE_BASE_PATH,
  VERIFY_PROFILE_BASE_PATH,
} from './constants';

export function isObject(value) {
  return value != null && typeof value === 'object' && value.constructor === Object;
}

export function setWindowLocation(location) {
  window.location.href = location;
}

export function hasParsedResume(profileData) {
  const { resume, upload_parse_status: uploadParseStatus } = profileData;
  return (
    resume
    // resume might be {}, so check if it isn't just an empty object
    && (typeof resume === 'object' && Object.keys(resume).length)
    // do not proceed if the parse failed for whatever reason
    && uploadParseStatus !== 'failed'
  );
}

// this is used when determining if we should show normal profile ui or verifying their parsed resume
export function shouldVerifyResume(profileData, replaceProfile) {
  if (!profileData) {
    return false;
  }
  const { has_profile: hasProfile } = profileData;
  return (
    // first, check if their resume can be verified
    hasParsedResume(profileData) && (
      // case if they don't have a profile yet
      !hasProfile
      // case if they elected to replace their profile
      || replaceProfile
    )
  );
}

export function isVerifyLocation(location) {
  const match = matchPath(location.pathname, {
    path: '/profile/verify',
  });
  return !!match;
}

export function addBasicInfoStep() {
  return !!(new URLSearchParams(window.location.search).get('basic_info_step'));
}

export function addPasswordFormStep() {
  return !!(new URLSearchParams(window.location.search).get('password_form_step'));
}

export function addEligibilityCelebrationStep() {
  return !!(new URLSearchParams(window.location.search).get('eligibility_celebration_step'));
}

export function getEncQuizId() {
  return new URLSearchParams(window.location.search).get('enc_quiz_id');
}

export function isApplyIntercept() {
  return !!(new URLSearchParams(window.location.search).get('apply_intercept'));
}

export function isOnboardingFlow() {
  return !!(new URLSearchParams(window.location.search).get('onboarding_flow'));
}

export function isPostApplyLocation(location) {
  const match = matchPath(location.pathname, {
    path: '/profile/post-apply',
  });
  const encQuizId = getEncQuizId();
  return !!match && !!encQuizId;
}

// does it look like /profile/verify or /profile/post-apply?enc_quiz_id=12345
export function isVerifyFlowLocation(location) {
  return isVerifyLocation(location) || isPostApplyLocation(location);
}

export function toggleMainHeader(showHeader) {
  // certain steps in verify mode displays its own headers
  if (showHeader) {
    $('.main_site_header > *').show();
  } else {
    $('.main_site_header > *').hide();
  }
  $(window).trigger('header-adjust');
}

// update the location depending on the step
export function correctLocation(inVerifyFlow, history) {
  // compare the step/flow (which is based on the actual profile data) with the landed location
  const isCorrectLocation = inVerifyFlow === isVerifyFlowLocation(window.location);
  // if correct, leave as is
  // this also preserves paths that have more function when first landing
  // i.e.
  // /profile/post-apply?quiz_id=12345
  // /profile/edit/about-me
  if (isCorrectLocation) {
    return;
  }
  // if got here, update the route appropriate to the step
  if (inVerifyFlow) {
    history.replace(VERIFY_PROFILE_BASE_PATH);
  } else {
    history.replace(PROFILE_BASE_PATH);
  }
}

export function addStoreSectionItem(section, data) {
  return [
    // spread existing items
    ...section,
    // add the newly created item
    { reactKey: shortid.generate(), ...data },
  ];
}

export function addPatchSectionItem(section, data) {
  return [
    // spread existing items
    ...section,
    // add the newly created item
    { ...data },
  ];
}

export function editStoreSectionItem(section, data, reactKey) {
  return [
    // filter and spread the unedited items
    ...section.filter((item) => item.reactKey !== reactKey),
    // add the newly edited item
    { ...data, reactKey },
  ];
}

export function editPatchSectionItem(section, data, reactKey) {
  return [
    // filter and spread the unedited items
    ...section.filter((item) => item.reactKey !== reactKey),
    // add the newly edited item
    { ...data },
  ];
}

export function removeSectionItem(section, reactKey) {
  return section.filter((item) => item.reactKey !== reactKey);
}

export function getPatchSuccessMessage(storeData, patchData) {
  if (patchData.email_address && patchData.email_address !== storeData.email_address) {
    return `Profile was successfully updated. Please check ${patchData.email_address} for a confirmation email.`;
  }

  // custom success message for skills section
  if (patchData.skills && patchData.skills !== storeData.skills) {
    return 'Your skills have been updated.';
  }

  return 'Profile was successfully updated.';
}

export function filterStoreDataKeys(profileData, data, displayKeys) {
  const newData = { ...data };
  // filter unchanged data keys
  Object.keys(displayKeys).forEach((displayKey) => {
    if (newData[displayKey] === profileData[displayKey]) {
      delete newData[displayKeys[displayKey]];
    }
  });
  return newData;
}

export function filterPatchDataKeys(data, displayKeys) {
  // filter display keys from patch data
  return Object.keys(data).reduce((obj, key) => {
    if (key in displayKeys || key === '#displayKeys') { return obj; }
    return {
      ...obj,
      [key]: data[key],
    };
  }, {});
}

export function flattenObjects(data) {
  let flattenedData = { ...data };
  // flatten single-depth objects into the request body
  Object.keys(flattenedData).forEach((key) => {
    const keyData = flattenedData[key];
    if (isObject(keyData)) {
      delete flattenedData[key];
      flattenedData = {
        ...keyData,
        ...flattenedData,
      };
    }
  });
  return flattenedData;
}

export function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const compareDateDesc = (a, b) => {
  const aDate = a && new Date(a);
  const bDate = b && new Date(b);
  if (aDate && bDate) {
    return compareDesc(aDate, bDate);
  }
  // prioritize the one with non-null date
  if (aDate) {
    return -1;
  }
  if (bDate) {
    return 1;
  }
  return 0;
};

export const compareDateRangeDesc = (aStart, aEnd, bStart, bEnd) => {
  const aEndDate = aEnd && new Date(aEnd);
  const bEndDate = bEnd && new Date(bEnd);

  if (aEndDate && bEndDate) {
    const result = compareDesc(aEndDate, bEndDate);
    if (result !== 0) {
      return result;
    }
    // if equal, then compare startDates too
    return compareDateDesc(aStart, bStart);
  }
  // prioritize the one with non-null date
  if (aEndDate) {
    return -1;
  }
  if (bEndDate) {
    return 1;
  }
  return compareDateDesc(aStart, bStart);
};

export function normalizeProfileData(profileData) {
  const arrayedData = [
    'job_history',
    'school_history',
    'licenses_and_certifications',
    'associations',
    'achievements',
  ];

  const normalizedData = { ...profileData };

  arrayedData.forEach((section) => {
    // if undefined, initialize the section with an empty array
    if (!profileData[section]) {
      normalizedData[section] = [];
    }
  });

  return normalizedData;
}

export function formatSection(updatedSection, updatedSectionKeys) {
  // create patch data object with patch_ keys and normal keys
  const patchData = updatedSectionKeys
    .filter((key) => !key.startsWith('store_'))
    .reduce((obj, key) => {
      if (key.startsWith('patch_')) {
        return {
          ...obj,
          [key.substring(6)]: updatedSection[key],
        };
      }

      return {
        ...obj,
        [key]: updatedSection[key],
      };
    }, {});

  // create store data object with store_ keys and normal keys
  const storeData = updatedSectionKeys
    .filter((key) => !key.startsWith('patch_'))
    .reduce((obj, key) => {
      if (key.startsWith('store_')) {
        return {
          ...obj,
          [key.substring(6)]: updatedSection[key],
        };
      }

      return {
        ...obj,
        [key]: updatedSection[key],
      };
    }, {});

  return { patchData, storeData };
}

export function handleDatePrecision(section, precision) {
  return Object.keys(section).reduce((obj, key) => {
    const newObj = obj;

    // add key to object
    newObj[key] = section[key];

    // add precision field if date is in values
    if (key.endsWith('date')) {
      newObj[`${key}_precision`] = precision;
    }

    return newObj;
  }, {});
}

/**
 * sorting logic from app/lib/StarterView/Util/Profile/XMLDataConverter.pm
 */
export function getSortedSection(section) {
  return [...section].sort((a, b) => {
    // current first
    if (a.current && !b.current) {
      return -1;
    }
    if (!a.current && b.current) {
      return 1;
    }

    // no end_date first
    if (!a.end_date && b.end_date) {
      return -1;
    }
    if (a.end_date && !b.end_date) {
      return 1;
    }

    // no start_date first
    if (!a.start_date && b.start_date) {
      return -1;
    }
    if (a.start_date && !b.start_date) {
      return 1;
    }

    // no issued_date first
    if (!a.issued_date && b.issued_date) {
      return -1;
    }
    if (a.issued_date && !b.issued_date) {
      return 1;
    }

    // reverse chronological on end_date
    if (a.end_date && isIso8601Date(a.end_date) && b.end_date && isIso8601Date(b.end_date)) {
      const sort = b.end_date.localeCompare(a.end_date);
      if (sort) { return sort; }
    }

    // reverse chronological on start_date
    if (a.start_date && isIso8601Date(a.start_date) && b.start_date && isIso8601Date(b.start_date)) {
      const sort = b.start_date.localeCompare(a.start_date);
      if (sort) { return sort; }
    }

    // reverse chronological on issued_date
    if (a.issued_date && isIso8601Date(a.issued_date) && b.issued_date && isIso8601Date(b.issued_date)) {
      const sort = b.issued_date.localeCompare(a.issued_date);
      if (sort) { return sort; }
    }

    // alphabetical on employer
    if (a.employer && b.employer) {
      const sort = a.employer.toLowerCase().localeCompare(b.employer.toLowerCase());
      if (sort) { return sort; }
    }

    // alphabetical on position
    if (a.position && b.position) {
      const sort = a.position.toLowerCase().localeCompare(b.position.toLowerCase());
      if (sort) { return sort; }
    }

    // alphabetical on school
    if (a.school && b.school) {
      const sort = a.school.toLowerCase().localeCompare(b.school.toLowerCase());
      if (sort) { return sort; }
    }

    // alphabetical on issuing_authority
    if (a.issuing_authority && b.issuing_authority) {
      const sort = a.issuing_authority.toLowerCase().localeCompare(b.issuing_authority.toLowerCase());
      if (sort) { return sort; }
    }

    // alphabetical on name
    if (a.name && b.name) {
      const sort = a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      if (sort) { return sort; }
    }

    return 0;
  });
}

export function calculateProfileCompletion(profile, hasReferences) {
  let completion = 16; // automatic with 'register with ziprecruiter'
  const profileJso = profile.data;
  if (profileJso) {
    completion += profileJso.resume ? 17 : 0;
    completion += profileJso.phone_number ? 17 : 0;
    completion += profileJso.skills.length ? 17 : 0;
    completion += profileJso.headline ? 17 : 0;
  }
  completion += hasReferences ? 16 : 0;
  return completion;
}

export function isProfileComplete(profile, hasReferences) {
  return !!(
    profile.data
    && profile.data.resume
    && profile.data.phone_number
    && profile.data.skills.length
    && profile.data.headline
    && hasReferences
  );
}
